import {
  getPlanningFinancialField,
  IJBPPlanning,
  IPlanningData,
  isCanonicalPlanningRow,
  isPlanningBaselineRow,
  isPlanningCategoryTotal,
  isPlanningPlanRow,
  isPlanningSubCategoryTotal,
  TPlanningFinancialField,
} from 'partner360/entities/planning/model'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { IFinancialPlanQualityData } from '../../model/types'

const FULL_YEAR_QUARTER = 5
const OVERALL_CATEGORY = 'jbp-overall'
const getNodeId = (parentId: string, nodeId: string): string => `${parentId}\0${nodeId}`

const getNumber = (
  row: IJBPPlanning | undefined,
  field: TPlanningFinancialField | null
): number => {
  if (!row || field === null) {
    return 0
  }

  return Number(row[field]) || 0
}

const getGrowth = (rows: IJBPPlanning[], field: 'FinYoYGrowth' | 'SobYoYGrowth'): number | null => {
  const value = rows.find((row) => typeof row[field] === 'number')?.[field]

  return typeof value === 'number' ? value : null
}

interface IBuildFinancialPlanQualityDataParams {
  rows: IJBPPlanning[]
  fYear: number
  actualsType: ActualsValueTypes
  isLocalCurrency?: boolean
  settings?: IPlanningData['Settings']
}

export const buildFinancialPlanQualityData = ({
  rows,
  fYear,
  actualsType,
  isLocalCurrency = false,
  settings = [],
}: IBuildFinancialPlanQualityDataParams): IFinancialPlanQualityData[] => {
  const measureField = getPlanningFinancialField(actualsType, isLocalCurrency)
  const categoryRows = rows.filter(
    (row) => isCanonicalPlanningRow(row) && isPlanningCategoryTotal(row)
  )
  const subCategoryRows = rows.filter(
    (row) => isCanonicalPlanningRow(row) && isPlanningSubCategoryTotal(row)
  )
  const categoryOrderByKey = new Map<string, number>()
  const subCategoryOrderByKey = new Map<string, number>()
  const sobAvailabilityByKey = new Map<string, boolean>()
  settings
    .filter(({ IsMainHierarchy }) => IsMainHierarchy === 1)
    .forEach(({ Category, CategoryOrder, SubCategory, SubCategoryOrder, IsSoBAvailable }) => {
      const category = Category.trim()
      const subCategory = SubCategory.trim()
      const currentCategoryOrder = categoryOrderByKey.get(category)
      const subCategoryKey = getNodeId(category, subCategory)
      const currentSubCategoryOrder = subCategoryOrderByKey.get(subCategoryKey)

      sobAvailabilityByKey.set(subCategoryKey, IsSoBAvailable === 1)

      if (currentCategoryOrder === undefined || CategoryOrder < currentCategoryOrder) {
        categoryOrderByKey.set(category, CategoryOrder)
      }
      if (
        subCategory &&
        subCategory !== OVERALL_CATEGORY &&
        (currentSubCategoryOrder === undefined || SubCategoryOrder < currentSubCategoryOrder)
      ) {
        subCategoryOrderByKey.set(subCategoryKey, SubCategoryOrder)
      }
    })
  const categories = [...new Set(categoryRows.map((row) => row.Category.trim()).filter(Boolean))]

  return categories.map((category) => {
    const rowsForCategory = categoryRows.filter((row) => row.Category.trim() === category)
    const buildData = (
      nodeRows: IJBPPlanning[],
      id: string,
      label: string
    ): IFinancialPlanQualityData => {
      const nodePlanRow = nodeRows.find((row) => isPlanningPlanRow(row, fYear, FULL_YEAR_QUARTER))
      const nodeBaselineRow = nodeRows.find((row) =>
        isPlanningBaselineRow(row, fYear, FULL_YEAR_QUARTER)
      )
      const currentYearRows = nodeRows.filter((row) => row.FYear === fYear)

      return {
        id,
        category: label,
        plan: getNumber(nodePlanRow, measureField),
        baseline: getNumber(nodeBaselineRow, measureField),
        sobPlan: Number(nodePlanRow?.SoB) || 0,
        sobBaseline: Number(nodeBaselineRow?.SoB) || 0,
        finYoyGrowth: getGrowth(currentYearRows, 'FinYoYGrowth'),
        sobYoyGrowth: getGrowth(currentYearRows, 'SobYoYGrowth'),
      }
    }

    const data = buildData(rowsForCategory, category, category)
    const categorySobAvailability = sobAvailabilityByKey.get(getNodeId(category, OVERALL_CATEGORY))
    if (categorySobAvailability !== undefined) data.isSobAvailable = categorySobAvailability
    const categoryOrder = categoryOrderByKey.get(category)
    if (categoryOrder !== undefined) data.order = categoryOrder
    const categoryRow = rowsForCategory.find((row) => row.FYear === fYear && Boolean(row.IsTarget))

    if (categoryRow?.PartnerId && categoryRow.ProgramTypes) {
      data.planningLink = {
        partnerId: categoryRow.PartnerId,
        fYear,
        programType: categoryRow.ProgramTypes,
        category,
      }
    }

    const hasSubCategoryPlanning = rowsForCategory.some(
      (row) => row.FYear === fYear && Boolean(row.IsTarget) && row.IsCollapsed === 0
    )

    if (hasSubCategoryPlanning) {
      const childRows = subCategoryRows.filter((row) => row.Category.trim() === category)
      const subCategories = [
        ...new Set(childRows.map((row) => row.SubCategory.trim()).filter(Boolean)),
      ]

      const children = subCategories.map((subCategory) => {
        const child = buildData(
          childRows.filter((row) => row.SubCategory.trim() === subCategory),
          getNodeId(category, subCategory),
          subCategory
        )
        const subCategoryOrder = subCategoryOrderByKey.get(getNodeId(category, subCategory))
        if (subCategoryOrder !== undefined) child.order = subCategoryOrder
        const subCategorySobAvailability = sobAvailabilityByKey.get(
          getNodeId(category, subCategory)
        )
        if (subCategorySobAvailability !== undefined) {
          child.isSobAvailable = subCategorySobAvailability
        }

        return child
      })

      if (children.length) data.children = children
    }

    return data
  })
}
